import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';

import { API_ENDPOINT } from '../../../constants';
import { fetchWrapper } from '../../../helpers';
import { EVV_HISTORY_LOOKBACK_DAYS } from '../constants';

import type { EvvHistoryResponse, EvvSessionRow } from '../types';

export function useEvvHistory() {
  const { getAccessTokenSilently } = useAuth0();
  const [sessions, setSessions] = useState<EvvSessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [history, setHistory] = useState<EvvHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const listController = useRef<AbortController | null>(null);
  const historyController = useRef<AbortController | null>(null);

  const selectSession = useCallback(
    (sessionId: number | null) => {
      historyController.current?.abort();
      setSelectedSessionId(sessionId);
      setHistory(null);
      setHistoryError(null);
      setHistoryLoading(sessionId !== null);
      if (sessionId === null) return;

      const controller = new AbortController();
      historyController.current = controller;
      void fetchWrapper
        .get({
          url: `${API_ENDPOINT}/evv/sessions/${sessionId}/history`,
          getAccessTokenSilently,
          signal: controller.signal,
        })
        .then((response: EvvHistoryResponse) => {
          if (!controller.signal.aborted) setHistory(response);
        })
        .catch((error: Error) => {
          if (!controller.signal.aborted) {
            setHistoryError(error.message || 'Unable to load EVV history');
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setHistoryLoading(false);
        });
    },
    [getAccessTokenSilently],
  );

  const reload = useCallback(
    (startDate: string, endDate: string) => {
      listController.current?.abort();
      selectSession(null);
      setSessions([]);
      setSessionError(null);
      if (
        !moment(startDate, 'YYYY-MM-DD', true).isValid() ||
        !moment(endDate, 'YYYY-MM-DD', true).isValid() ||
        startDate > endDate
      ) {
        setLoading(false);
        setSessionError('Enter a valid date range');
        return;
      }

      const controller = new AbortController();
      listController.current = controller;
      setLoading(true);
      const params = new URLSearchParams({
        start: moment(startDate).startOf('day').toISOString(),
        end: moment(endDate).endOf('day').toISOString(),
      });
      void fetchWrapper
        .get({
          url: `${API_ENDPOINT}/sessions/evv-history/?${params}`,
          getAccessTokenSilently,
          signal: controller.signal,
        })
        .then((response: EvvSessionRow[]) => {
          if (!controller.signal.aborted) setSessions(response);
        })
        .catch((error: Error) => {
          if (!controller.signal.aborted)
            setSessionError(error.message || 'Unable to load sessions');
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    },
    [getAccessTokenSilently, selectSession],
  );

  useEffect(() => {
    reload(
      moment().subtract(EVV_HISTORY_LOOKBACK_DAYS, 'days').format('YYYY-MM-DD'),
      moment().format('YYYY-MM-DD'),
    );
    return () => {
      listController.current?.abort();
      historyController.current?.abort();
    };
  }, [reload]);

  return {
    sessions,
    loading,
    sessionError,
    selectedSessionId,
    history,
    historyLoading,
    historyError,
    selectSession,
    reload,
  };
}
